import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  Input,
  FormControl,
  FormLabel,
  Avatar,
  Badge,
  Divider,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FiEdit, FiSave, FiX, FiUser, FiMail, FiPhone, FiMapPin, FiLock, FiAlertTriangle } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { useGetUserProfile, useUpdateUserProfile, useChangePassword, useDeleteAccount } from "../../services/userProfileService";
import { formatRoleName } from "../../utils/roleFormatter";

// Change Password Form Component
const ChangePasswordForm = ({ onSubmit, isLoading, onCancel }) => {
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.current_password) newErrors.current_password = "Current password is required";
    if (!formData.new_password) newErrors.new_password = "New password is required";
    if (formData.new_password.length < 6) newErrors.new_password = "Password must be at least 6 characters";
    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      current_password: formData.current_password,
      new_password: formData.new_password,
      confirm_password: formData.confirm_password
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4}>
        <FormControl isInvalid={!!errors.current_password}>
          <FormLabel>Current Password</FormLabel>
          <Input
            type="password"
            value={formData.current_password}
            onChange={(e) => setFormData({...formData, current_password: e.target.value})}
            placeholder="Enter current password"
          />
          {errors.current_password && (
            <Text color="red.500" fontSize="sm">{errors.current_password}</Text>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.new_password}>
          <FormLabel>New Password</FormLabel>
          <Input
            type="password"
            value={formData.new_password}
            onChange={(e) => setFormData({...formData, new_password: e.target.value})}
            placeholder="Enter new password"
          />
          {errors.new_password && (
            <Text color="red.500" fontSize="sm">{errors.new_password}</Text>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.confirm_password}>
          <FormLabel>Confirm New Password</FormLabel>
          <Input
            type="password"
            value={formData.confirm_password}
            onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
            placeholder="Confirm new password"
          />
          {errors.confirm_password && (
            <Text color="red.500" fontSize="sm">{errors.confirm_password}</Text>
          )}
        </FormControl>

        <HStack spacing={3} w="100%">
          <Button variant="outline" onClick={onCancel} flex={1}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            colorScheme="blue" 
            isLoading={isLoading}
            loadingText="Changing Password..."
            flex={1}
          >
            Change Password
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};

// Delete Account Form Component
const DeleteAccountForm = ({ onSubmit, isLoading, onCancel }) => {
  const [formData, setFormData] = useState({
    password: "",
    reason: ""
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.reason) newErrors.reason = "Reason is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4}>
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Warning!</Text>
            <Text fontSize="sm">
              This action cannot be undone. All your data will be permanently deleted.
            </Text>
          </Box>
        </Alert>

        <FormControl isInvalid={!!errors.password}>
          <FormLabel>Confirm Password</FormLabel>
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            placeholder="Enter your password to confirm"
          />
          {errors.password && (
            <Text color="red.500" fontSize="sm">{errors.password}</Text>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.reason}>
          <FormLabel>Reason for Deletion</FormLabel>
          <Textarea
            value={formData.reason}
            onChange={(e) => setFormData({...formData, reason: e.target.value})}
            placeholder="Please provide a reason for deleting your account"
            rows={3}
          />
          {errors.reason && (
            <Text color="red.500" fontSize="sm">{errors.reason}</Text>
          )}
        </FormControl>

        <HStack spacing={3} w="100%">
          <Button variant="outline" onClick={onCancel} flex={1}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            colorScheme="red" 
            isLoading={isLoading}
            loadingText="Deleting Account..."
            flex={1}
          >
            Delete Account
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};

const ProfileSection = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const toast = useToast();

  // Get user context data
  const { userRole, companyInfo } = useUserContext();

  // Get user profile from API
  const { profileData, profileIsLoading, refetchProfile } = useGetUserProfile();
  const { updateProfilePayload, updateProfileIsLoading } = useUpdateUserProfile(() => {
    refetchProfile();
    setIsEditing(false);
  });

  // Change password and delete account hooks
  const { changePasswordPayload, changePasswordIsLoading } = useChangePassword(() => {
    setShowChangePasswordModal(false);
  });

  const { deleteAccountPayload, deleteAccountIsLoading } = useDeleteAccount(() => {
    setShowDeleteAccountModal(false);
    // Redirect to login after account deletion
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  });

  // Get user data from localStorage as fallback
  const getStoredUserData = () => {
    try {
      const authData = sessionStorage.getItem("auth_data");
      if (authData) {
        const parsed = JSON.parse(authData);
        return {
          name: parsed?.user?.name || parsed?.user?.full_name || "User",
          email: parsed?.user?.email || "",
          phone: parsed?.user?.phone || "",
          company: parsed?.user?.company_name || parsed?.user?.company || "",
          role: parsed?.user?.role || "User",
        };
      }
    } catch (error) {
      console.error("Error parsing stored user data:", error);
    }
    return null;
  };

  const storedUserData = getStoredUserData();

  // Combine user data from context, API, and localStorage
  const userData = {
    name: profileData?.name || profileData?.full_name || storedUserData?.name || "User",
    email: profileData?.email || storedUserData?.email || "",
    phone: profileData?.phone || profileData?.phone_number || storedUserData?.phone || "",
    company: companyInfo?.name || profileData?.company || storedUserData?.company || "",
    role: userRole || profileData?.role || storedUserData?.role || "User",
    avatar: profileData?.avatar || profileData?.profile_image || null,
    location: profileData?.location || profileData?.address || "",
    timezone: profileData?.timezone || "UTC",
  };

  const [formData, setFormData] = useState(userData);

  // Update form data when user data changes
  useEffect(() => {
    setFormData(userData);
  }, [userData]);

  const handleEdit = () => {
    setFormData(userData);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(userData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      // Prepare payload for API
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        timezone: formData.timezone,
      };

      // Call API to update profile
      await updateProfilePayload(payload);
      
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Failed to update profile. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = (passwordData) => {
    changePasswordPayload(passwordData);
  };

  const handleDeleteAccount = (deleteData) => {
    deleteAccountPayload(deleteData);
  };

  // Loading state
  if (profileIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">Loading profile information...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Profile Information
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Manage your personal information and account details
          </Text>
        </VStack>
        {!isEditing ? (
          <Button
            leftIcon={<FiEdit />}
            colorScheme="blue"
            variant="outline"
            onClick={handleEdit}
          >
            Edit Profile
          </Button>
        ) : (
          <HStack spacing={2}>
            <Button
              leftIcon={<FiX />}
              variant="outline"
              onClick={handleCancel}
              isDisabled={updateProfileIsLoading}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="blue"
              onClick={handleSave}
              isLoading={updateProfileIsLoading}
              loadingText="Saving..."
            >
              Save Changes
            </Button>
          </HStack>
        )}
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Profile Card */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <HStack spacing={4} align="center">
                <Avatar
                  size="xl"
                  name={userData.name}
                  src={userData.avatar}
                  bg="blue.500"
                />
                <VStack align="start" spacing={1}>
                  <Heading size="md" color="gray.900">
                    {isEditing ? formData.name : userData.name}
                  </Heading>
                  <Badge colorScheme="blue" variant="subtle">
                    {formatRoleName(userData.role)}
                  </Badge>
                  <Text color="gray.600" fontSize="sm">
                    {userData.company}
                  </Text>
                </VStack>
              </HStack>

              <Divider />

              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                    Full Name
                  </FormLabel>
                  {isEditing ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <HStack spacing={2}>
                      <FiUser size={16} color="gray.400" />
                      <Text color="gray.900">{userData.name}</Text>
                    </HStack>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                    Email Address
                  </FormLabel>
                  {isEditing ? (
                    <Input
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email"
                      type="email"
                    />
                  ) : (
                    <HStack spacing={2}>
                      <FiMail size={16} color="gray.400" />
                      <Text color="gray.900">{userData.email}</Text>
                    </HStack>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                    Phone Number
                  </FormLabel>
                  {isEditing ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <HStack spacing={2}>
                      <FiPhone size={16} color="gray.400" />
                      <Text color="gray.900">{userData.phone}</Text>
                    </HStack>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                    Location
                  </FormLabel>
                  {isEditing ? (
                    <Input
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Enter your location"
                    />
                  ) : (
                    <HStack spacing={2}>
                      <FiMapPin size={16} color="gray.400" />
                      <Text color="gray.900">{userData.location}</Text>
                    </HStack>
                  )}
                </FormControl>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Account Details Card */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Account Details
              </Heading>

              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Company
                  </Text>
                  <Text color="gray.900">{userData.company}</Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Role
                  </Text>
                  <Badge colorScheme="blue" variant="subtle">
                    {formatRoleName(userData.role)}
                  </Badge>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Timezone
                  </Text>
                  <Text color="gray.900">{userData.timezone}</Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.700" mb={1}>
                    Account Status
                  </Text>
                  <Badge colorScheme="green" variant="subtle">
                    Active
                  </Badge>
                </Box>
              </VStack>

              <Divider />

              <VStack spacing={3} align="stretch">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  Change Password
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  colorScheme="red"
                  onClick={() => setShowDeleteAccountModal(true)}
                >
                  Delete Account
                </Button>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Change Password Modal */}
      <Modal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChangePasswordForm 
              onSubmit={handleChangePassword}
              isLoading={changePasswordIsLoading}
              onCancel={() => setShowChangePasswordModal(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Account Modal */}
      <Modal isOpen={showDeleteAccountModal} onClose={() => setShowDeleteAccountModal(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Account</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <DeleteAccountForm 
              onSubmit={handleDeleteAccount}
              isLoading={deleteAccountIsLoading}
              onCancel={() => setShowDeleteAccountModal(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ProfileSection; 